import type {
  RouteWithDetails,
  RouteConnectionWithDetails,
} from '@sembo-private/maps';
import { ConnectionType } from '@sembo-private/maps';

import { getHumanizedDuration } from '@apps/main/modules/helpers/formatting';
import type { HotelIngredient } from '@apps/main/modules/recipe/ingredients';
import type { OwnStayIngredient } from '@apps/main/modules/recipe/ingredients/ownStayIngredient';

import type {
  IngredientId,
  HotelIngredientResponseWithStaticInfo,
  OwnStayIngredientResponseWithStaticInfo,
} from '@libs/trip-planner-interfaces';

import type {
  ItineraryNoTransportSegment,
  ItinerarySimpleTransportSegment,
  ItinerarySimpleTransportSegmentType,
  ItineraryStenaUpsellNoTransportSegment,
  ItineraryUpsellNoTransportSegment,
} from '../../models';
import { ItinerarySegmentType } from '../../models';

import { getRoute } from './tripOverviewHotelRelatedSegmentsLogic';
import { getCountryText } from './tripOverviewLocationSegmentsLogic';

export interface GetTripOverviewNoTransportSegmentParams {
  ingredientId: IngredientId;
  ingredientIndex: number | undefined;
  isReturnTransport: boolean;
  isDesktop: boolean;
  isLastSegment: boolean;
  isUserFromBookingPage: boolean;
  isUserFromStena: boolean;
  displayText: string;
  mainPolygonFullName?: string;
}

export function getTripOverviewNoTransportSegment(
  params: GetTripOverviewNoTransportSegmentParams
):
  | ItineraryNoTransportSegment
  | ItineraryUpsellNoTransportSegment
  | ItineraryStenaUpsellNoTransportSegment {
  if (params.ingredientIndex === 0 && !params.isUserFromBookingPage) {
    if (params.isUserFromStena) {
      return {
        segmentType: ItinerarySegmentType.StenaUpsellNoTransport,
        ingredientId: params.ingredientId,
      };
    } else if (params.isDesktop) {
      return {
        segmentType: ItinerarySegmentType.UpsellNoTransport,
        ingredientId: params.ingredientId,
        destination: getCountryText(
          params.displayText,
          params.mainPolygonFullName
        ),
      };
    }
  }

  return {
    segmentType: ItinerarySegmentType.NoTransport,
    ingredientId: params.ingredientId,
    isReturnTransport: params.isReturnTransport,
    isLastSegment: params.isLastSegment,
  };
}

interface GetTripOverviewSimpleTransportSegmentParams {
  ingredient:
    | HotelIngredient
    | OwnStayIngredient
    | HotelIngredientResponseWithStaticInfo
    | OwnStayIngredientResponseWithStaticInfo;
  calculatedRoute: RouteWithDetails;
  isLastSegment: boolean;
}

export function getTripOverviewSimpleTransportSegment(
  params: GetTripOverviewSimpleTransportSegmentParams
): ItinerarySimpleTransportSegment {
  let route: RouteConnectionWithDetails | undefined;
  let transport: ConnectionType;
  const ingredient = params.ingredient;
  const isIngredientResponseWithStaticInfo = 'staticInfo' in ingredient;

  if (isIngredientResponseWithStaticInfo) {
    route =
      isIngredientResponseWithStaticInfo && ingredient.staticInfo
        ? getRoute(ingredient.staticInfo, params.calculatedRoute)
        : params.calculatedRoute.at(-1);
    transport = ingredient.transportToBeReached;
  } else {
    route = params.calculatedRoute.at(-1);
    transport = ingredient.transportTypeToBeReached;
  }

  return {
    segmentType: getItinerarySegmentTypeFromConnectionType(transport),
    ingredientId: params.ingredient.ingredientId,
    isLastSegment: params.isLastSegment,
    transport:
      route?.distanceInKilometers && route.expectedTravelTimeInMinutes
        ? {
            distance: `${route.distanceInKilometers.toFixed()}km`,
            time: getHumanizedDuration(
              route.expectedTravelTimeInMinutes,
              'minute'
            ),
          }
        : undefined,
  };
}

export function isSimpleTransport(connectionType: ConnectionType): boolean {
  const simpleTransports = [
    ConnectionType.CAR,
    ConnectionType.BICYCLE,
    ConnectionType.WALK,
  ];

  return simpleTransports.includes(connectionType);
}

export function getItinerarySegmentTypeFromConnectionType(
  transport: ConnectionType
): ItinerarySimpleTransportSegmentType {
  switch (transport) {
    case ConnectionType.CAR:
      return ItinerarySegmentType.Car;
    case ConnectionType.BICYCLE:
      return ItinerarySegmentType.Cycle;
    case ConnectionType.WALK:
      return ItinerarySegmentType.Walk;
    default:
      throw new Error('No Transport in hotel Ingredient');
  }
}
